import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'
import { deleteConversation } from './messageSlice.js'

const API_VERSION = 'v1'
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api/${API_VERSION}`

// Create group
export const createGroup = createAsyncThunk(
	'groups/createGroup',
	async (data) => {
		try {
			const res = await fetch(`${REQUEST_URL}/groups/create-group`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(data),
			})
			return await res.json()
		} catch (err) {
			console.error(err.message)
		}
	}
)

// Leave group
export const leaveGroup = createAsyncThunk(
	'groups/leaveGroup',
	async (groupId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/groups/leave-group/${groupId}`,
				{
					method: 'PATCH',
					credentials: 'include',
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err)
		}
	}
)

// Get user's groups
export const getUserGroups = createAsyncThunk(
	'groups/getUserGroups',
	async ({ kazooId, search }) => {
		try {
			const queryParams = new URLSearchParams()
			if (kazooId) queryParams.append('kazooId', kazooId)
			if (search) queryParams.append('search', search)

			const res = await fetch(`${REQUEST_URL}/groups/get-groups?${queryParams.toString()}`, {
				method: 'GET',
				credentials: 'include',
			})
			return await res.json()
		} catch (err) {
			console.error(err.message)
		}
	}
)

// Add member to group
export const addGroupMember = createAsyncThunk(
	'group/addGroupMember',
	async ({ groupId, userIds }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/groups/add-member-to-group/${groupId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ userIds }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const fetchGroupMembers = createAsyncThunk(
	'groups/fetchGroupMembers',
	async (groupId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/groups/get-group-members/${groupId}`,
				{
					method: 'GET',
					credentials: 'include',
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const fetchGroupMessages = createAsyncThunk(
	'groups/fetchGroupMessages',
	async (groupId) => {
		const res = await fetch(
			`${REQUEST_URL}/group-message/get-group-messages/${groupId}`,
			{
				credentials: 'include',
				method: 'GET',
			}
		)
		const data = await res.json()
		return data
	}
)

export const sendGroupMessage = createAsyncThunk(
	'groupMessages/send',
	async (data) => {
		const isFormData = data instanceof FormData
		const res = await fetch(
			`${REQUEST_URL}/group-message/send-group-message/${isFormData ? data.get('groupId') : data.groupId}`,
			{
				method: 'POST',
				credentials: 'include',
				headers: {
					...(isFormData ? {} : { 'Content-Type': 'application/json' }),
				},
				body: isFormData ? data : JSON.stringify({ text: data.text }),
			}
		)
		return res.json()
	}
)

export const updateGroup = createAsyncThunk(
	'group/updateGroup',
	async ({ groupId, data }) => {
		const fileData = new FormData()
		for (const key in data) {
			if (!data[key]) {
				continue
			}
			fileData.append(String(key), data[key])
		}

		try {
			const res = await fetch(
				`${REQUEST_URL}/groups/update-group/${groupId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					// headers: {
					// 	'Content-Type': 'application/json',
					// },
					body: fileData,
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const muteUnmuteGroupNotification = createAsyncThunk(
	'groups/muteUnmuteGroupNotification',
	async ({ groupId, is_mute_chat_notification }) => {
		try {
			const res = await fetch(
				`${process.env.REACT_APP_REQUEST_URL}/api/v1/accounts/mute-unmute-chat-group-notification/${groupId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ is_mute_chat_notification }),
				}
			)
			const result = await res.json()
			return { ...result, groupId, isMuted: is_mute_chat_notification }
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const markGroupMessagesAsRead = createAsyncThunk(
	'groups/markGroupMessagesAsRead',
	async ({ groupId }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/group-message/mark-as-read/${groupId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			result.groupId = groupId
			return result
		} catch (err) {
			console.error(err.message)
		}
	}
)

const initialState = {
	groups: [],
	messages: [],
	groupMembers: [],
	currentSelectedGroup: null,
	unreadCounts: {}, // Track unread counts per group { groupId: count }
}

const groupSlice = createSlice({
	name: 'groups',
	initialState,
	reducers: {
		setCurrentSelectedGroup: (state, { payload }) => {
			state.currentSelectedGroup = payload
			if (payload?._id) {
				// Clear unread count locally when group is selected
				delete state.unreadCounts[payload._id]
				const groupIndex = state.groups.findIndex(g => g._id === payload._id)
				if (groupIndex !== -1) {
					state.groups[groupIndex].unreadCount = 0
				}
			}
		},
		// Append a new incoming message to the current list (e.g. from a socket)
		updateCurrentGroupMessages: (state, { payload }) => {
			state.messages = [payload, ...state.messages]

			// Update latest message in groups list for real-time sorting
			state.groups = state.groups.map(group => {
				if (group._id === payload.groupId) {
					return {
						...group,
						latestMessage: payload
					}
				}
				return group
			})
		},
		// Update unread count for a specific group
		updateGroupUnreadCount: (state, { payload }) => {
			const { groupId, count } = payload
			if (count > 0) {
				state.unreadCounts[groupId] = count
			} else {
				delete state.unreadCounts[groupId]
			}

			// Update in groups list
			const groupIndex = state.groups.findIndex(g => g._id === groupId)
			if (groupIndex !== -1) {
				state.groups[groupIndex].unreadCount = count
			}
		},
		// Update latest message in group list when new message arrives
		updateGroupListMessage: (state, { payload }) => {
			const { message, currentUserId } = payload

			// Find group in groups list
			const groupIndex = state.groups.findIndex(g => g._id === message.groupId)

			if (groupIndex !== -1) {
				// Group exists - update latest message and move to top
				const group = state.groups[groupIndex]

				const isSent = String(message.senderId) === String(currentUserId)
				let text = message.text ? message.text : message.fileUrl ? 'Attachment' : ''
				if (isSent && text) {
					text = 'You: ' + text
				} else if (!isSent && text && message.name) {
					text = message.name + ': ' + text
				}

				group.latestMessage = {
					...message,
					text
				}

				// Move to top of list
				state.groups.splice(groupIndex, 1)
				state.groups.unshift(group)
			}
		},
		// Add newly created group to the list
		addNewGroup: (state, { payload }) => {
			const { group } = payload
			// Add to beginning of groups list
			state.groups.unshift(group)
		},
	},
	extraReducers: (builder) => {
		builder

			.addCase(createGroup.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to create new group chat',
						'error'
					)
					return
				}
				toastHelper('Group chat created successfully', 'success')

				state.groups.push(payload.group)
			})

			.addCase(getUserGroups.fulfilled, (state, { payload }) => {
				state.groups = payload.groups || []
			})

			.addCase(addGroupMember.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to add member',
						'error'
					)
					return
				}
				toastHelper('Member added!', 'success')
				state.groupMembers.push(...payload.newMembers)
			})

			.addCase(fetchGroupMembers.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to fetch group',
						'error'
					)
					return
				}
				state.groupMembers = payload.groupMembers
			})

			.addCase(fetchGroupMessages.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to fetch group messages',
						'error'
					)
					return
				}
				state.messages = payload.messages
			})

			.addCase(sendGroupMessage.pending, (state, { meta }) => {
				const { text, groupId, tempId, senderId } = meta.arg
				if (!tempId || !senderId) return // Skip if not optimistic

				const optimisticMsg = {
					_id: tempId,
					tempId,
					senderId,
					groupId,
					text,
					status: 'sending',
					createdAt: new Date().toISOString(),
				}

				// 1. Update message list
				state.messages = [optimisticMsg, ...state.messages]

				// 2. Update sidebar
				state.groups = state.groups.map(group => {
					if (group._id === groupId) {
						return {
							...group,
							latestMessage: {
								...optimisticMsg,
								text: 'You: ' + (text || '📎Attachment')
							}
						}
					}
					return group
				})
			})

			.addCase(sendGroupMessage.fulfilled, (state, { payload, meta }) => {
				if (!payload || !payload.success) {
					// Mark optimistic message as failed if it exists
					const tempId = meta.arg?.tempId
					if (tempId) {
						state.messages = state.messages.map(m =>
							m.tempId === tempId ? { ...m, status: 'failed' } : m
						)
					}
					toastHelper(
						payload.message || 'Failed to send message',
						'error'
					)
					return
				}

				const tempId = meta.arg?.tempId
				if (tempId) {
					// Replace optimistic message with actual message
					state.messages = state.messages.map(m =>
						m.tempId === tempId ? payload.sentMessage : m
					)
				} else {
					// Fallback
					state.messages = [payload.sentMessage, ...state.messages]
				}

				// Update latest message in groups list
				state.groups = state.groups.map(group => {
					if (group._id === payload.sentMessage.groupId) {
						return {
							...group,
							latestMessage: {
								...payload.sentMessage,
								text: 'You: ' + (payload.sentMessage.text || (payload.sentMessage.fileUrl ? "📎Attachment" : ''))
							}
						}
					}
					return group
				})
			})
			.addCase(leaveGroup.fulfilled, (state, { payload }) => {
				const { groupId } = payload
				if (!payload.success) {
					toastHelper(
						payload.message || 'Failed to leave group',
						'error'
					)
				}

				toastHelper(
					payload.message || 'Left group successfully',
					'success'
				)

				// Remove from group list
				state.groups = state.groups.filter((g) => g._id !== groupId)
				state.currentSelectedGroup = null
				state.groupMembers = []
				state.messages = []
			})

			.addCase(updateGroup.fulfilled, (state, { payload }) => {
				if (!payload.success) {
					toastHelper(
						payload.message || 'Failed to update group',
						'error'
					)
				}
				toastHelper(
					payload.message || 'Successfully updated group',
					'success'
				)

				state.groups = state.groups.map((group) => {
					if (group._id === payload.group._id) return payload.group
					return group
				})
				state.currentSelectedGroup = payload.group
			})

			.addCase(deleteConversation.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success || !payload.groupId) {
					return
				}
				// Clear messages for the group conversation
				state.messages = []

				// Update the group to remove the latest message but keep the group in the list
				state.groups = state.groups.map((group) => {
					if (group._id === payload.groupId) {
						return {
							...group,
							latestMessage: null
						}
					}
					return group
				})

				// Clear messages if this is the currently selected group
				if (state.currentSelectedGroup?._id === payload.groupId) {
					state.currentSelectedGroup = {
						...state.currentSelectedGroup,
						latestMessage: null
					}
				}
			})

			.addCase(markGroupMessagesAsRead.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}

				// Update unread count to 0 for this group
				const groupId = payload.groupId
				delete state.unreadCounts[groupId]

				// Update in groups list
				const groupIndex = state.groups.findIndex(g => g._id === groupId)
				if (groupIndex !== -1) {
					state.groups[groupIndex].unreadCount = 0
				}
			})

			.addCase(muteUnmuteGroupNotification.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to mute/unmute notification',
						'error'
					)
					return
				}
				toastHelper(payload.message, 'success')

				if (state.currentSelectedGroup?._id === payload.groupId) {
					state.currentSelectedGroup = {
						...state.currentSelectedGroup,
						isMuted: payload.isMuted,
					}
				}

				state.groups = state.groups.map((group) => {
					if (group._id === payload.groupId) {
						return { ...group, isMuted: payload.isMuted }
					}
					return group
				})
			})
	},
})
export const {
	setCurrentSelectedGroup,
	updateCurrentGroupMessages,
	updateGroupUnreadCount,
	updateGroupListMessage,
	addNewGroup
} = groupSlice.actions
export default groupSlice.reducer
